import React, { FC, useEffect } from "react";

import "./style.scss";

// Commented out enum kept for reference
// enum LinkTargetType {
//     Unknown = 0,
//     Screen,
//     Dialog,
// }

/**
 * Interface defining a link target with its type
 * @interface LinkTarget
 * @property {string} target - The target identifier
 * @property {any} type - The type of target (screen, dialog, etc.)
 */
interface LinkTarget {
    target: string;
    type: any;
}

/**
 * Props interface for the Link component
 * @interface LinkProps
 * @property {string} text - The text to display for the link
 * @property {string | LinkTarget[]} target - The target(s) to navigate to when clicked
 * @property {string} [className] - Optional CSS class for styling
 * @property {string[]} [users] - Optional array of users associated with the link
 * @property {string | LinkTarget[]} [failure] - Optional fallback target(s) if primary target fails
 * @property {string[]} [visibleTo] - Optional array of user roles that can see this link
 * @property {(target: string | LinkTarget[], shiftKey: boolean, users: string[], failure: string | LinkTarget[]) => void} [onClick] - Click handler
 * @property {() => void} [onRendered] - Optional callback when component is rendered
 */
export interface LinkProps {
    text: string;
    target: string | LinkTarget[];
    className?: string;
    users?: [];
    failure?: string | LinkTarget[];
    visibleTo?: string[];

    onClick?: (target: string | LinkTarget[], shiftKey: boolean, users: string[], failure: string | LinkTarget[]) => void;
    onRendered?: () => void;
}

/**
 * A interactive link component that supports both mouse and touch interactions.
 * Can navigate to different targets and handle multi-touch events.
 * 
 * @component
 * @example
 * ```tsx
 * <Link 
 *   text="Go to Home"
 *   target="home-screen"
 *   onClick={(target, isShift, users, failure) => handleNavigation(target)}
 * />
 * ```
 */
const Link: FC<LinkProps> = (props) => {
    const { text, target, className, users, failure, onClick, onRendered } = props;
    const css = ["__link__", className ? className : null].join(" ").trim();

    /** Tracks number of simultaneous touches */
    let touches = 0;

    /**
     * Handles the start of touch events
     * @param {React.TouchEvent<HTMLSpanElement>} e - The touch event
     */
    const handleTouchStart = (e: React.TouchEvent<HTMLSpanElement>) => {
        touches = e.touches.length;
    };
    /**
     * Handles the end of touch events, preventing default click behavior
     * @param {React.TouchEvent<HTMLSpanElement>} e - The touch event
     */
    const handleTouchEnd = (e: React.TouchEvent<HTMLSpanElement>) => {
        e.preventDefault(); // prevents the click event firing
        onClick && onClick(target, touches > 1, users, failure);
        touches = 0;
    };

    /**
     * Handles mouse click events
     * @param {React.MouseEvent<HTMLSpanElement>} e - The mouse event
     */
    const handleClick = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.preventDefault();
        onClick && onClick(target, e.shiftKey, users, failure);
    };
    /**
     * Triggers the onRendered callback if provided
     */
    const handleRendered = () => (onRendered && onRendered());

    // Call handleRendered on mount/update
    useEffect(() => handleRendered());

    return (
        <span
            className={css}
            onClick={handleClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            {text}
        </span>
    );
};

export default Link;
